import { Fragment } from 'react'
import { generatePath, Link, useMatches } from 'react-router'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb'
import { type TKey, useTranslation } from '@/translations/i18n'

interface BreadcrumbHandle {
	fullPath: string
	breadcrumb: TKey
}

function hasBreadcrumb(handle: unknown): handle is BreadcrumbHandle {
	return (
		typeof handle === 'object' &&
		handle !== null &&
		'fullPath' in handle &&
		typeof handle.fullPath === 'string' &&
		'breadcrumb' in handle &&
		typeof handle.breadcrumb === 'string'
	)
}

interface MarqueeBreadcrumbsProps {
	/**
	 * Trailing crumb resolved from dynamic data (e.g. an event title) rather
	 * than a route handle. When provided it becomes the current page and every
	 * handle-based crumb is rendered as a link.
	 */
	currentLabel?: string
}

export function MarqueeBreadcrumbs({ currentLabel }: MarqueeBreadcrumbsProps) {
	const { t } = useTranslation()
	const matches = useMatches()

	// Every handle-based crumb resolves to a real path, so links always carry a
	// string `to`.
	const handleCrumbs = matches
		.filter((match) => hasBreadcrumb(match.handle))
		.map((match) => {
			const handle = match.handle as BreadcrumbHandle
			return {
				label: t(handle.breadcrumb),
				to: generatePath(handle.fullPath, match.params)
			}
		})

	// The current page is either the dynamic label (detail pages) or the last
	// handle crumb; everything before it renders as a link.
	const currentPage = currentLabel ?? handleCrumbs.at(-1)?.label
	const links = currentLabel ? handleCrumbs : handleCrumbs.slice(0, -1)

	if (!currentPage) {
		return null
	}

	return (
		<Breadcrumb>
			<BreadcrumbList className='gap-2 text-xs'>
				{links.map((link) => (
					<Fragment key={link.to}>
						<BreadcrumbItem>
							<BreadcrumbLink asChild>
								<Link
									className='text-neutral-900 hover:underline'
									to={link.to}
								>
									{link.label}
								</Link>
							</BreadcrumbLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator className='text-neutral-500 [&>svg]:size-3' />
					</Fragment>
				))}
				<BreadcrumbItem>
					<BreadcrumbPage className='text-neutral-500'>
						{currentPage}
					</BreadcrumbPage>
				</BreadcrumbItem>
			</BreadcrumbList>
		</Breadcrumb>
	)
}
